import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const ContactSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    storeUrl: '',
    revenue: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({
          title: "Thanks! We'll be in touch within 48 hours.",
          description: "Your inquiry has been received."
        });
        setFormData({ name: '', email: '', storeUrl: '', revenue: '', message: '' });
      } else {
        throw new Error('Server error');
      }
    } catch (error) {
      // Fallback to mailto on network/server error
      const subject = encodeURIComponent(`New Inquiry from ${formData.name} — Super Boring Company Website`);
      const body = encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\nAmazon Store URL: ${formData.storeUrl || 'Not provided'}\nAnnual Revenue Range: ${formData.revenue}\n\nMessage:\n${formData.message}`
      );
      window.location.href = `mailto:yc@superboring.in?subject=${subject}&body=${body}`;
      toast({
        title: "Opening your email client...",
        description: "Please send the email to complete your inquiry."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#1B4D3E] px-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        <h2
          className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#F5F0EB] mb-6"
          style={{ fontFamily: 'Space Grotesk, sans-serif' }}>

          Own a boring Amazon business? Let's talk.
        </h2>
        
        <p className="text-xl text-[#E8E2DB] mb-16 leading-relaxed">We are Amazon operators at heart and would love to hear your story irrespective of whether you want to sell or not.

        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Input
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full bg-transparent border-2 border-[#F5F0EB] text-[#F5F0EB] placeholder:text-[#E8E2DB] focus:border-[#FFFFFF] py-6 text-lg rounded-none"
                placeholder="Name" />

            </div>

            <div>
              <Input
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full bg-transparent border-2 border-[#F5F0EB] text-[#F5F0EB] placeholder:text-[#E8E2DB] focus:border-[#FFFFFF] py-6 text-lg rounded-none"
                placeholder="Email" />

            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Input
                name="storeUrl"
                type="text"
                value={formData.storeUrl}
                onChange={handleChange}
                className="w-full bg-transparent border-2 border-[#F5F0EB] text-[#F5F0EB] placeholder:text-[#E8E2DB] focus:border-[#FFFFFF] py-6 text-lg rounded-none"
                placeholder="Amazon Store URL (optional)" />

            </div>

            <div className="relative select-wrapper">
              <select
                name="revenue"
                value={formData.revenue}
                onChange={handleChange}
                required
                className="w-full bg-transparent border-2 border-[#F5F0EB] text-[#F5F0EB] py-6 px-5 text-lg focus:border-[#FFFFFF] focus:outline-none cursor-pointer"
                style={{
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  backgroundImage: 'none',
                  backgroundColor: 'transparent',
                  paddingRight: '3rem'
                }}>

                <option value="" disabled className="bg-[#1B4D3E] text-[#F5F0EB]">Annual Revenue Range</option>
                <option value="under-200k" className="bg-[#1B4D3E] text-[#F5F0EB]">Under $200K</option>
                <option value="200k-500k" className="bg-[#1B4D3E] text-[#F5F0EB]">$200K–$500K</option>
                <option value="500k-1m" className="bg-[#1B4D3E] text-[#F5F0EB]">$500K–$1M</option>
                <option value="1m-2m" className="bg-[#1B4D3E] text-[#F5F0EB]">$1M–$2M</option>
                <option value="2m-plus" className="bg-[#1B4D3E] text-[#F5F0EB]">$2M+</option>
              </select>
              <div className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-[#F5F0EB] text-sm">
                ▾
              </div>
            </div>
          </div>

          <div>
            <Textarea
              name="message"
              required
              value={formData.message}
              onChange={handleChange}
              rows={6}
              className="w-full bg-transparent border-2 border-[#F5F0EB] text-[#F5F0EB] placeholder:text-[#E8E2DB] focus:border-[#FFFFFF] resize-none text-lg rounded-none"
              placeholder="Tell us about your business..." />

          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="px-8 py-6 text-lg font-medium bg-[#F5F0EB] text-[#1B4D3E] hover:bg-white rounded-none">

            {isSubmitting ? 'Sending...' :
            <>
                Send it <ArrowRight className="ml-2 w-5 h-5" strokeWidth={2} />
              </>
            }
          </Button>
        </form>

        <div className="mt-12 text-center">
          <p className="text-[#E8E2DB]">
            Or just email us at{' '}
            <a
              href="mailto:yc@superboring.in"
              className="text-[#F5F0EB] font-medium underline hover:text-white">

              yc@superboring.in
            </a>
            {' '}— we read every message.
          </p>
        </div>
      </div>
    </section>);

};

export default ContactSection;